import React, { useEffect, useRef, useState } from "react";
import usePipelinesApi, { LabelItem } from "../Pipeline/use-pipelines";
import { useToast } from "../..";
import { Container, ListLabels } from "./styles";
import { Button } from "../../";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { v } from "../../../util";

type UpdateLabelProps = {
  labels: LabelItem[];
  onClose: () => void;
  onSuccess: () => void;
};

type DraftLabelItem = {
  labelId?: number;
  tempId: string;
  name: string;
  color: string;
  isNew: boolean;
};

type UpdateLabelFormValues = {
  labels: DraftLabelItem[];
};

const updateLabelSchema = v.object({
  labels: v.array(
    v.object({
      labelId: v.number().optional(),
      tempId: v.string(),
      name: v.string().superRefine(v.nonempty()),
      color: v.string(),
      isNew: v.boolean()
    })
  )
});

const UpdateLabel: React.FC<UpdateLabelProps> = ({
  labels,
  onClose,
  onSuccess
}) => {
  const toast = useToast();
  const { deleteLabel, saveLabels } = usePipelinesApi();
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;
  const companyId = user.admin ? company?.id : user.companyId;

  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [labelIdToDelete, setLabelIdToDelete] = useState<number | null>(null);

  const colorInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors }
  } = useForm<UpdateLabelFormValues>({
    defaultValues: {
      labels: []
    },
    resolver: zodResolver(updateLabelSchema),
    shouldFocusError: true
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "labels"
  });

  const getRandomSuggestedColor = () => {
    const colors = [
      "#EF4444",
      "#F59E0B",
      "#10B981",
      "#3B82F6",
      "#8B5CF6",
      "#EC4899",
      "#14B8A6",
      "#84CC16"
    ];

    return colors[Math.floor(Math.random() * colors.length)];
  };

  const startCreateLabel = () => {
    append({
      tempId: `${Date.now()}-${Math.random()}`,
      name: "",
      color: getRandomSuggestedColor(),
      isNew: true
    });
  };

  const openColorPicker = (tempId: string) => {
    setTimeout(() => {
      const input = colorInputRefs.current[tempId] as
        | (HTMLInputElement & { showPicker?: () => void })
        | null;

      if (!input) return;

      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click();
      }
    }, 0);
  };

  const handleDeleteLabel = async () => {
    if (!labelIdToDelete) return;

    try {
      await deleteLabel(companyId, labelIdToDelete);

      const currentLabels = getValues("labels");
      const updatedLabels = currentLabels.filter(
        (item) => item.labelId !== labelIdToDelete
      );

      replace(updatedLabels);

      setConfirmCloseModal(false);
      setLabelIdToDelete(null);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso!",
        detail: "Etiqueta excluída com sucesso!"
      });

      onSuccess();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao excluir Etiqueta.";

      setConfirmCloseModal(false);
      setLabelIdToDelete(null);

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const hasLabelChanges = (currentLabels: DraftLabelItem[]) => {
    if (currentLabels.length !== labels.length) {
      return true;
    }

    const originalLabelsSorted = [...labels]
      .map((item) => ({
        labelId: item.id,
        name: item.name ?? "",
        color: normalizeHexColor(item.color)
      }))
      .sort((a, b) => (a.labelId ?? 0) - (b.labelId ?? 0));

    const currentLabelsSorted = [...currentLabels]
      .map((item) => ({
        labelId: item.labelId,
        name: item.name.trim(),
        color: normalizeHexColor(item.color)
      }))
      .sort((a, b) => (a.labelId ?? 0) - (b.labelId ?? 0));

    return (
      JSON.stringify(originalLabelsSorted) !==
      JSON.stringify(currentLabelsSorted)
    );
  };

  const handleSaveAll = handleSubmit(async (data) => {
    try {
      const canEditPipeline = user.admin || hasAnyPermission("pipeline_edit");

      if (!canEditPipeline) {
        toast?.current?.show({
          life: 5000,
          severity: "warn",
          summary: "Aviso",
          detail: "Você não tem permissão para editar etiquetas."
        });
        return;
      }

      if (!hasLabelChanges(data.labels)) {
        onClose();
        return;
      }

      const payload = data.labels.map((item) => ({
        ...(item.labelId ? { id: item.labelId } : {}),
        name: item.name,
        color: item.color
      }));

      await saveLabels(companyId, payload);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso!",
        detail: "Etiquetas salvas com sucesso!"
      });

      onSuccess();
      onClose();
    } catch (e: any) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao salvar etiquetas."
      });
    }
  });

  useEffect(() => {
    reset({
      labels: labels.map((item) => ({
        labelId: item.id,
        tempId: String(item.id),
        name: item.name ?? "",
        color: item.color ?? "#D9D9D9",
        isNew: false
      }))
    });
  }, [labels, reset]);

  const normalizeHexColor = (color?: string) => {
    if (!color) return "#D9D9D9";

    const value = color.trim();

    if (/^#[0-9a-fA-F]{6}$/.test(value)) {
      return value;
    }

    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
      return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
    }

    return "#D9D9D9";
  };

  return (
    <Container>
      <ListLabels>
        <div className="helperText">Clique na cor para alterá-la</div>

        <div className="divisor">
          <div className="listLabels">
            {fields.map((item, index) => (
              <div key={item.tempId} className="labelRow">
                <button
                  type="button"
                  className="colorDotButton"
                  onClick={() => openColorPicker(item.tempId)}
                >
                  <Controller
                    name={`labels.${index}.color`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <div
                          className="colorDot"
                          style={{
                            backgroundColor: normalizeHexColor(field.value)
                          }}
                        />
                        <input
                          ref={(el) => {
                            colorInputRefs.current[item.tempId] = el;
                          }}
                          type="color"
                          value={normalizeHexColor(field.value)}
                          className="hiddenColorInput"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </>
                    )}
                  />
                </button>

                <div className="labelInputWrapper">
                  <Controller
                    name={`labels.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Nome da label"
                        className="inlineInput"
                      />
                    )}
                  />

                  {errors.labels?.[index]?.name?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.labels[index]?.name?.message}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="deleteButton"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (item.isNew) {
                      remove(index);
                      return;
                    }

                    setLabelIdToDelete(item.labelId ?? null);
                    setConfirmCloseModal(true);
                  }}
                >
                  <i className="pi pi-trash" />
                </button>
              </div>
            ))}

            <div className="addLabelButton" onClick={startCreateLabel}>
              <i className="pi pi-plus" style={{ fontSize: "14px" }} />{" "}
              Adicionar etiqueta
            </div>
          </div>
        </div>

        <div className="buttons">
          <Button
            label="VOLTAR"
            className="no-hover-button"
            onClick={onClose}
          />
          <Button
            label="SALVAR"
            onClick={handleSaveAll}
            style={{
              background: user.plataform.mainColor
                ? user.plataform.mainColor
                : "#2624CD",
              borderColor: user.plataform.mainColor
                ? user.plataform.mainColor
                : "#2624CD",
              color: "#fff"
            }}
          />
        </div>
      </ListLabels>

      <Dialog
        visible={confirmCloseModal}
        onHide={() => setConfirmCloseModal(false)}
        draggable={false}
        header="Deseja excluir a etiqueta?"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Ao excluir a etiqueta, a relação dele com todos os cards será
            perdida e essa ação não poderá ser desfeita. Tem certeza que deseja
            excluir?
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={handleDeleteLabel}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => {
                setConfirmCloseModal(false);
                setLabelIdToDelete(null);
              }}
            />
          </div>
        </div>
      </Dialog>
    </Container>
  );
};

export default UpdateLabel;
